import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { toISO, fromISO, addDays, DOW_SHORT, MONTH_SHORT } from "@/lib/dates";
import {
  Printer,
  ArrowLeft,
  CaretLeft,
  CaretRight,
  SteeringWheel,
  Backpack,
  Warning,
  Airplane,
  Bandaids,
  Prohibit,
  Truck,
} from "@phosphor-icons/react";

const ABSENCE_META = {
  holiday: { label: "HOL", icon: Airplane, bg: "bg-amber-400 text-amber-950" },
  sickness: { label: "SIC", icon: Bandaids, bg: "bg-rose-500 text-white" },
  absence: { label: "ABS", icon: Prohibit, bg: "bg-zinc-700 text-white" },
};

export default function PrintDayPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const dateParam = params.get("date");
  const initialDate = useMemo(() => {
    if (dateParam) {
      try {
        return fromISO(dateParam);
      } catch {}
    }
    return new Date(2026, 3, 8); // Weds 8 Apr 26
  }, [dateParam]);
  const [day, setDay] = useState(initialDate);
  const iso = toISO(day);

  const [routes, setRoutes] = useState([]);
  const [staff, setStaff] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [vehicleAssignments, setVehicleAssignments] = useState([]);
  const [vehicleServices, setVehicleServices] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s, a, ab, v, va, vs] = await Promise.all([
        api.listRoutes(),
        api.listStaff(),
        api.listAssignments({ start_date: iso, end_date: iso }),
        api.listAbsences({ start_date: iso, end_date: iso }),
        api.listVehicles(),
        api.listVehicleAssignments({ start_date: iso, end_date: iso }),
        api.listVehicleServices({ start_date: iso, end_date: iso }),
      ]);
      setRoutes(r);
      setStaff(s);
      setAssignments(a);
      setAbsences(ab);
      setVehicles(v);
      setVehicleAssignments(va);
      setVehicleServices(vs);
    } catch {
      toast.error("Failed to load day");
    } finally {
      setLoading(false);
    }
  }, [iso]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setParams({ date: iso }, { replace: true });
  }, [iso, setParams]);

  const staffById = useMemo(() => {
    const m = {};
    for (const x of staff) m[x.id] = x;
    return m;
  }, [staff]);

  const vehiclesById = useMemo(() => {
    const m = {};
    for (const x of vehicles) m[x.id] = x;
    return m;
  }, [vehicles]);

  const primaryVehicleByRoute = useMemo(() => {
    const m = {};
    for (const v of vehicles) if (v.route_id && !m[v.route_id]) m[v.route_id] = v;
    return m;
  }, [vehicles]);

  const overrideByRoute = useMemo(() => {
    const m = {};
    for (const va of vehicleAssignments) m[va.route_id] = va;
    return m;
  }, [vehicleAssignments]);

  const absenceByStaff = useMemo(() => {
    const m = {};
    for (const ab of absences) m[ab.staff_id] = ab;
    return m;
  }, [absences]);

  const assignmentMap = useMemo(() => {
    const m = {};
    for (const a of assignments) {
      if (!m[a.route_id]) m[a.route_id] = {};
      m[a.route_id][a.slot] = a.staff_id;
    }
    return m;
  }, [assignments]);

  const kpis = useMemo(() => {
    const totalSlots = routes.reduce((s, r) => s + 1 + r.loader_slots, 0);
    const filled = assignments.length;
    const absentAssigned = assignments.filter((a) => absenceByStaff[a.staff_id]).length;
    const vehiclesInService = vehicleServices.length;
    return {
      totalSlots,
      filled,
      gaps: totalSlots - filled,
      absentAssigned,
      vehiclesInService,
      overrides: vehicleAssignments.length,
    };
  }, [routes, assignments, absenceByStaff, vehicleServices, vehicleAssignments]);

  const dayLabel = `${DOW_SHORT[day.getDay()]} ${day.getDate()} ${MONTH_SHORT[day.getMonth()]} ${day.getFullYear()}`;

  return (
    <div className="print-page min-h-screen bg-white">
      {/* Toolbar */}
      <div className="no-print sticky top-0 z-10 border-b-2 border-black bg-white px-6 py-3 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/schedule")}
            data-testid="daily-print-back"
            className="flex items-center gap-1.5 px-3 py-2 border border-black text-xs font-mono-plex tracking-widest hover:bg-zinc-100 active:scale-[0.98]"
          >
            <ArrowLeft size={12} weight="bold" />
            BACK
          </button>
          <div className="font-heading font-black text-lg tracking-tight leading-none">
            DAILY BRIEF · {dayLabel}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-black">
            <button
              onClick={() => setDay((d) => addDays(d, -1))}
              data-testid="daily-print-prev"
              className="px-2 h-9 border-r border-black hover:bg-zinc-100 active:scale-[0.98]"
            >
              <CaretLeft size={14} weight="bold" />
            </button>
            <button
              onClick={() => setDay(new Date(2026, 3, 8))}
              className="px-3 h-9 border-r border-black hover:bg-zinc-100 text-[10px] font-mono-plex tracking-widest font-semibold"
            >
              JUMP
            </button>
            <button
              onClick={() => setDay((d) => addDays(d, 1))}
              data-testid="daily-print-next"
              className="px-2 h-9 hover:bg-zinc-100 active:scale-[0.98]"
            >
              <CaretRight size={14} weight="bold" />
            </button>
          </div>
          <button
            onClick={() => window.print()}
            data-testid="daily-print-btn"
            className="flex items-center gap-2 px-4 h-9 bg-black text-white text-xs font-mono-plex tracking-widest hover:bg-zinc-800 active:scale-[0.98]"
          >
            <Printer size={14} weight="bold" />
            PRINT
          </button>
        </div>
      </div>

      {/* Sheet */}
      <div className="max-w-[210mm] mx-auto p-6 print:p-3">
        <div className="border-b-2 border-black pb-3 mb-4 flex items-end justify-between">
          <div>
            <div className="font-heading font-black text-2xl tracking-tight leading-none">
              SM FORECAST · DEPOT DAILY BRIEF
            </div>
            <div className="font-mono-plex text-xs text-zinc-700 mt-2 tracking-widest">
              {dayLabel.toUpperCase()}
            </div>
          </div>
          <div className="font-mono-plex text-[10px] text-zinc-500 tracking-widest text-right">
            GENERATED {new Date().toISOString().slice(0, 10)}
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          <MiniKpi label="SLOTS FILLED" value={`${kpis.filled}/${kpis.totalSlots}`} />
          <MiniKpi label="GAPS" value={kpis.gaps} accent={kpis.gaps > 0 ? "rose" : null} />
          <MiniKpi label="ON ABSENCE (SCHEDULED)" value={kpis.absentAssigned} accent={kpis.absentAssigned > 0 ? "amber" : null} />
          <MiniKpi label="VEHICLE OVERRIDES" value={kpis.overrides} />
        </div>

        {loading ? (
          <div className="font-mono-plex text-xs text-zinc-500">LOADING…</div>
        ) : (
          <div className="grid grid-cols-2 gap-2" data-testid="daily-print-grid">
            {routes.map((route) => {
              const primary = primaryVehicleByRoute[route.id];
              const override = overrideByRoute[route.id];
              const displayVehicle = override ? vehiclesById[override.vehicle_id] : primary;
              const assign = assignmentMap[route.id] || {};
              const slots = ["driver", ...Array.from({ length: route.loader_slots }, (_, i) => `loader-${i + 1}`)];
              return (
                <div
                  key={route.id}
                  className="border-2 border-black p-2"
                  data-testid={`daily-print-card-${route.code}`}
                >
                  <div className="flex items-start justify-between gap-2 border-b border-black pb-1 mb-1.5">
                    <div>
                      <div className="font-heading font-black text-base leading-none">
                        {route.code}
                      </div>
                      <div className="text-[8px] tracking-widest text-zinc-600 mt-0.5">
                        GROUP {route.group}
                      </div>
                    </div>
                    {displayVehicle && (
                      <div
                        className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-[9px] font-mono-plex font-bold ${
                          override ? "border-emerald-700 text-emerald-800 bg-emerald-50" : "border-black bg-zinc-100"
                        }`}
                      >
                        <Truck size={10} weight="bold" />
                        {override ? "→ " : ""}
                        {displayVehicle.fleet_number}
                      </div>
                    )}
                  </div>
                  <div className="space-y-0.5">
                    {slots.map((slot) => {
                      const sid = assign[slot];
                      const staffObj = sid ? staffById[sid] : null;
                      const ab = sid ? absenceByStaff[sid] : null;
                      const isDriver = slot === "driver";
                      const label = isDriver ? "DRIVER" : `LOADER ${slot.split("-")[1]}`;
                      return (
                        <div key={slot} className="flex items-baseline gap-2 text-[10px] font-mono-plex">
                          <span className="text-[8px] tracking-widest text-zinc-500 w-14 shrink-0">
                            {label}
                          </span>
                          {isDriver ? (
                            <SteeringWheel size={9} weight="bold" className="text-zinc-500 shrink-0" />
                          ) : (
                            <Backpack size={9} weight="bold" className="text-zinc-400 shrink-0" />
                          )}
                          {staffObj ? (
                            <>
                              <span
                                className={`${isDriver ? "font-bold text-[11px]" : ""} ${
                                  ab ? "line-through text-zinc-500" : ""
                                }`}
                              >
                                {staffObj.name}
                              </span>
                              {ab && (
                                <span
                                  className={`text-[7px] tracking-widest font-bold px-1 py-0.5 leading-none ${
                                    ABSENCE_META[ab.type].bg
                                  }`}
                                >
                                  {ABSENCE_META[ab.type].label}
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-rose-700 font-bold tracking-widest">
                              <Warning size={9} weight="bold" />
                              EMPTY
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-4 pt-2 border-t border-black flex items-center justify-between gap-4 flex-wrap text-[9px] font-mono-plex text-zinc-600 tracking-widest">
          <div className="flex items-center gap-3 flex-wrap">
            <span>→ FLEET = VEHICLE OVERRIDE</span>
            <span>[HOL] [SIC] [ABS] = STAFF ABSENCE</span>
            <span>EMPTY = SLOT NEEDS COVER</span>
          </div>
          <div>SM PLANNER · DAILY {iso}</div>
        </div>
      </div>
    </div>
  );
}

function MiniKpi({ label, value, accent }) {
  return (
    <div
      className={`border-2 border-black px-2 py-1.5 ${
        accent === "rose" ? "bg-rose-50" : accent === "amber" ? "bg-amber-50" : "bg-white"
      }`}
    >
      <div className="text-[8px] font-mono-plex tracking-widest text-zinc-600">{label}</div>
      <div
        className={`font-heading font-black text-xl leading-none tracking-tight mt-1 ${
          accent === "rose" ? "text-rose-700" : ""
        }`}
      >
        {value}
      </div>
    </div>
  );
}
